import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from "react";
import axios from "axios";
import { hostname } from "../utils";
import { useWhitelabel } from "./WhitelabelContext";

// Tutorial page keys - these match the dashboard page routes
export const TUTORIAL_PAGES = {
  DASHBOARD: "dashboard",
  CHAT: "chat",
  EMPLOYEES: "employees",
  KNOWLEDGE_BASE: "knowledge_base",
  WORKFLOWS: "workflows",
  LISTS: "lists",
  SEQUENCES: "sequences",
  INBOX: "inbox",
  CONTENT_ENGINE: "content_engine",
  AGENTS: "agents",
  INTEGRATIONS: "integrations",
  COLLABORATORS: "collaborators",
  IMAGES: "images",
  AUDIO: "audio",
  VIDEO: "video",
  VOICES: "voices",
  PROMPT_STUDIO: "prompt_studio",
  PROFILE: "profile",
  COMPANIES: "companies",
  EXECUTIVE_ASSISTANT: "executive_assistant",
} as const;

export type TutorialPageKey = (typeof TUTORIAL_PAGES)[keyof typeof TUTORIAL_PAGES];

// Tutorial progress is a map of page keys to completion status
export type TutorialProgress = {
  [key in TutorialPageKey]?: boolean;
};

interface TutorialContextType {
  tutorialProgress: TutorialProgress;
  tutorialEnabled: boolean;
  isNewUser: boolean;
  loading: boolean;
  hasTutorialCompleted: (pageKey: TutorialPageKey) => boolean;
  markTutorialComplete: (pageKey: TutorialPageKey) => Promise<void>;
  resetTutorialProgress: () => Promise<void>;
  loadTutorialProgress: (getToken: () => Promise<string>) => Promise<void>;
  setTutorialProgress: React.Dispatch<React.SetStateAction<TutorialProgress>>;
  setIsNewUser: (isNew: boolean) => void;
}

const TutorialContext = createContext<TutorialContextType | undefined>(
  undefined
);

// Cutoff date - users created after this date will see tutorials
const TUTORIAL_CUTOFF_DATE = new Date("2026-01-18T00:00:00Z");

interface TutorialProviderProps {
  children: ReactNode;
  getToken: () => Promise<string>;
}

export const TutorialProvider: React.FC<TutorialProviderProps> = ({
  children,
  getToken,
}) => {
  const [tutorialProgress, setTutorialProgress] = useState<TutorialProgress>(
    {}
  );
  const [loading, setLoading] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);
  const { whitelabelSettings } = useWhitelabel();

  // Tutorials are enabled for new users on the main platform (not whitelabel)
  // Main platform has isMainSite: true, whitelabel domains have isMainSite: false or undefined
  const isMainPlatform = !whitelabelSettings || whitelabelSettings.isMainSite === true;
  const tutorialEnabled = isNewUser && isMainPlatform;

  const hasTutorialCompleted = useCallback(
    (pageKey: TutorialPageKey): boolean => {
      return tutorialProgress[pageKey] === true;
    },
    [tutorialProgress]
  );

  const loadTutorialProgress = useCallback(
    async (getTokenFn: () => Promise<string>) => {
      setLoading(true);
      try {
        const token = await getTokenFn();
        const response = await axios.get(`${hostname}/users/tutorial-progress`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setTutorialProgress(response.data.tutorialProgress || {});
      } catch (error) {
        console.error("Failed to load tutorial progress:", error);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const markTutorialComplete = useCallback(
    async (pageKey: TutorialPageKey) => {
      try {
        const token = await getToken();
        await axios.post(
          `${hostname}/users/tutorial-progress`,
          { pageKey, completed: true },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setTutorialProgress((prev) => ({ ...prev, [pageKey]: true }));
      } catch (error) {
        console.error("Failed to mark tutorial complete:", error);
      }
    },
    [getToken]
  );

  const resetTutorialProgress = useCallback(async () => {
    try {
      const token = await getToken();
      await axios.post(
        `${hostname}/users/tutorial-progress/reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTutorialProgress({});
    } catch (error) {
      console.error("Failed to reset tutorial progress:", error);
    }
  }, [getToken]);

  return (
    <TutorialContext.Provider
      value={{
        tutorialProgress,
        tutorialEnabled,
        isNewUser,
        loading,
        hasTutorialCompleted,
        markTutorialComplete,
        resetTutorialProgress,
        loadTutorialProgress,
        setTutorialProgress,
        setIsNewUser,
      }}
    >
      {children}
    </TutorialContext.Provider>
  );
};

export const useTutorial = (): TutorialContextType => {
  const context = useContext(TutorialContext);
  if (context === undefined) {
    throw new Error("useTutorial must be used within a TutorialProvider");
  }
  return context;
};

// Export the cutoff date for use elsewhere
export { TUTORIAL_CUTOFF_DATE };
